<script lang="ts">
	import uqnSplit from './functions/uqnSplit';
	import signAssign from './functions/signAssign';
	import fractionBuilder from './functions/fractionBuilder';
	import Fraction from './functions/fractionClass';
	import polyBuilder from './functions/polyBuilder';
	import katexify from './functions/katexify';
	//import brackets from './functions/brackets';

	export let uqn: number;

	let qn: string, substitution: string, initial: string, type4dydx: string;
	let t: number, z:string;
	$: [qn, substitution, initial, type4dydx] = uqnToQn1003(uqn);
	$: t = Number(uqn.toString()[0]);
	$: z = (t==1 || t==5) ? "x" : "z";

	function uqnToQn1003(u:number):string[]{
		let [t,i,a,b,initial] = uqnSplit(u);
		if (t==1){
			return type1Generator1003(i,a,b,initial);
		} else if (t==2){
			return type2Generator1003(i,a,b,initial);
		} else if (t==3){
			return type3Generator1003(i,a,b,initial);
		} else if (t==4){
			return type4Generator1003(i,a,b,initial);
		} else{
			return type5Generator1003(i,a,b,initial);
		}
	}
	function type1Generator1003(i:number,m:number,n:number,initial:number): string[]{
		// dydx = ( f(x) - h'(x) g(y) ) / ( g'(y) h(x) )
		// z = h(x) g(y)
		let qn: string;
		const [hx,gy,fx,hPrime,gPrime] = assignSubstitution(i,m,n);
		const sub = `z=${hx}${gy}`;
		if (i==2){ // h(x) = x^m, g(y) = y, f(x) = x^n, factorization of lowest power between m and n-1
			if ( (m-1) <= n ) {
				const newPower = n-m+1;
				const newX = polyBuilder([1],"x",newPower);
				const mString = m==1? "" : m.toString();
				qn = fractionBuilder(`${newX}-${mString}y`,'x')
			} else{
				const newPower = m-1-n;
				const newX = polyBuilder([m],"x",newPower);
				const newXTwo = polyBuilder([1],"x",newPower+1);
				qn = fractionBuilder(`1-${newX}y`,newXTwo);
			}
		} else {
			if (i==1||i==5){
				qn = fractionBuilder(`${fx}-${hPrime+gy}`,`${hx+gPrime}`)
			} else{
				qn = fractionBuilder(`${fx}-${hPrime+gy}`,`${gPrime+hx}`)
			}
		}
		let ini: string;
		if (i<4){ // x^n
			if (i==3){ // h(x) = e^x, x=0 initial
				ini = `{y=${initial}} \\allowbreak \\textrm{ when } \\allowbreak {x=0}`;
			} else{ // x = 1 initial
				let frac = new Fraction(1,n+1);
				if (i==0 && m != 1){ // y^m
					ini = `{y=\\left(${frac.add(initial).toString()}\\right)^{\\frac{1}{${m}}}} \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
				} else if (i==1){
					ini = `{y=\\ln\\left(${frac.add(initial).toString()}\\right)} \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
				} else{ // y
					ini = `{y=${frac.add(initial).toString()}} \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
				}
			}
		} else{ // e^x
			if (i==4 && m!=1){
				ini = `{y=\\left(e+${initial}\\right)^{\\frac{1}{${m}}}} \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
			} else if (i==5){
				ini = `{y=\\ln\\left(e+${initial}\\right)} \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
			} else{
				ini = `{y=e+${initial}} \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
			}
		}
		return ["\\displaystyle \\frac{\\mathrm{d}y}{\\mathrm{d}x}="+qn, sub, ini]
	}
	function assignSubstitution(i:number,m:number,n:number):string[]{
		let hx: string, gy: string, fx: string, gPrime: string, hPrime: string;
		const xn = n==1? "x" : `x^${n.toString()}`
		const ym = m==1? "y" : `y^${m.toString()}`
		const xm = m==1? "x" : `x^${m.toString()}`
		fx = i < 4 ? xn : "e^x";
		if (i%4==1 || i%4 ==0){
			hx = "x";
			hPrime = "";
			if (i%2==0){
				gy = ym;
				gPrime = polyBuilder([m],"y",m-1);
				gPrime = gPrime == "1"? "" : gPrime;
			} else{
				gy = "e^y";
				gPrime = gy;
			}
		} else{
			gy = "y";
			gPrime = "";
			if (i%2==0){
				hx = xm;
				hPrime = polyBuilder([m],"x",m-1);
				hPrime = hPrime == "1"? "" : hPrime;
			} else{
				hx = "e^x";
				hPrime = hx;
			}
		}
		return [hx, gy, fx, hPrime, gPrime]
	}
	function type2Generator1003(i:number,a:number,b:number,initial:number){
		const subType = a%4;
		const m = a<4 ? 2 : 3;
		const [hx,gy,_,hPrime,gPrime] = assignSubstitution(subType,m,m); // last argument not used
		const z = subType < 3 ? hx+gy : gy+hx;
		let fz: string, ini: string;
		switch(i){
			case 0:
				fz = `${z}+${b}`;
				if (subType==3){
					ini = (initial-b).toString();
				} else{
					ini = `${polyBuilder([initial],"e",1)}-${b}`;
				}
				break;
			case 1:
				fz = `${b*b}-\\left(${z}\\right)^2`;
				ini = new Fraction(b*initial-b,1+initial).simplify().toString();
				break;
			case 2:
				fz = `\\left(${z}\\right)^2-${b*b}`;
				ini = new Fraction(b*(initial+1)+b,1-(initial+1)).simplify().toString();
				break;
			case 3:
				fz = `\\left(${z}\\right)^2+${b*b}`;
				if (subType==3){
					ini = `${polyBuilder([b],`\\tan ${initial}`,1)}`
				} else{
					ini = `${polyBuilder([b],`\\tan ${initial+b}`,1)}`
				}
				break;
			case 4:
				fz = `\\sqrt{${b*b}-\\left(${z}\\right)^2}`;
				if (subType==3){
					ini = `${polyBuilder([b],`\\sin${initial}`,1)}`
				} else{
					ini = `${polyBuilder([b],`\\sin${initial+b}`,1)}`
				}
				break;
			case 5:
				fz = `\\cos^2 \\left(${z}\\right)`;
				const argument = subType==3 ? initial : initial+1;
				ini = argument==1 ? "\\frac{\\pi}{4}" : `\\tan^{-1}${argument}`;
				break;
			default:
				throw 'Error: i bigger than 5';
		}
		if (subType==0){
			ini = `\\left( ${ini} \\right)^{\\frac{1}{${m}}}`;
		} else if (subType==1){
			ini = `\\ln \\left( ${ini} \\right)`;
		}
		ini = `{y = ${ini} } \\allowbreak \\textrm{ when } \\allowbreak`;
		if (subType==3){
			ini += "{x=0}";
		} else{
			ini += "{x=1}";
		}
		let qn: string;
		if (subType==0){
			qn = fractionBuilder(`${fz}-${hPrime+gy}`,`${gPrime+hx}`);
		} else{
			qn = fractionBuilder(`${fz}-${hPrime+gy}`,`${hx+gPrime}`);
		}
		const sub = subType < 3 ? `z=${hx}${gy}` : `z=${gy+hx}`;
		return ["\\displaystyle \\frac{\\mathrm{d}y}{\\mathrm{d}x}="+qn, sub, ini]
	}
	function type3Generator1003(i:number,a:number,b:number,initial:number){
		let qn: string;
		const ax = polyBuilder([a],'x',1);
		const by = polyBuilder([b],'y',1);
		const axPlusby = `${ax}+${by}`;
		const sub = `z=${ax}+${by}`;
		let ini = ""
		if (i==0){ // a-z
			qn = `-${ax}-${by}`;
			if (b!=1){
				qn = fractionBuilder(qn,b);
			}
			ini = new Fraction(a-initial,b).simplify().toString();
		} else{
			const trigos = ["\\tan ", "\\cot ", "\\sec ", "\\cosec "];
			qn = `${trigos[i-1]}(${axPlusby})-${a}`;
			const arcTrigos = ["\\sin^{-1} ", "\\cos^{-1} ", "\\sin^{-1} ", "\\cos^{-1} "]
			ini = arcTrigos[i-1] + initial.toString();
			if (b!=1){
				qn = fractionBuilder(qn,b);
				ini = fractionBuilder(ini,b);
			}
		}
		ini = `{y = ${ini} } \\allowbreak \\textrm{ when } \\allowbreak {x=0}`;
		return ["\\displaystyle \\frac{\\mathrm{d}y}{\\mathrm{d}x}="+qn, sub, ini]
	}
	function type4Generator1003(i:number,a:number,b:number,initial:number){
		let qn: string;
		[a,b] = signAssign(i,a,b);
		const sub = `z=${dydx()}`;
		const oneMinusBOverA = new Fraction(1-b,a).simplify().toString();
		qn = `\\frac{\\mathrm{d}^2y}{\\mathrm{d}x^2}`;
		if (a>0){
			qn += "+" + polyBuilder([a],dydx(),1);
		} else{
			qn += polyBuilder([a],dydx(),1);
		}
		if (b>0){
			qn += `+${b}`;
		} else{
			qn += b.toString();
		}
		let ini = new Fraction(1,a*a).add(initial).toString();
		ini = `{y = ${ini} } \\allowbreak \\textrm{ when } \\allowbreak {x=0}`;
		return ["\\displaystyle "+qn+"=0", sub, ini, oneMinusBOverA]
	}
	function type5Generator1003(i:number,m:number,b:number,initial:number){
		let qn: string;
		const sub = m==1? 'y=zx' : `y=zx^${m}`;
		qn = `x${dydx()}=${polyBuilder([-b,m],'y')}`;
		let ini = new Fraction(b,m).add(initial).toString();
		ini = `{y = ${ini} } \\allowbreak \\textrm{ when } \\allowbreak {x=1}`;
		return ["\\displaystyle "+qn, sub, ini];
	}
	function dydx(y="y"):string{
		return `\\displaystyle\\frac{\\mathrm{d}${y}}{\\mathrm{d}x}`
	}

</script>

<p> 
	A differential equation is given by
</p>
<p class="qnpart">
	{@html katexify(qn+".")}
</p>
<p>
	(a) Use the substitution {@html katexify(substitution)} to show that the differential equation can be reduced into the form
	{@html katexify(`${dydx("z")}=f(${z})`)},
	where
	{@html katexify(`f(${z})`)}
	is a function to be determined.
</p>
{#if t==4}
<p>
	(b) Find {@html katexify('z')} in terms of {@html katexify('x')}, given that {@html katexify(`${dydx()}=${type4dydx}`)} when {@html katexify("x=0.")}
</p>
<p>
	(c) Hence find {@html katexify('y')} in terms of {@html katexify('x')}, given that
	{@html katexify(initial+".",false)}
</p>
{:else}
<p>
	(b) Hence solve the differential equation to find {@html katexify('y')} in terms of {@html katexify('x')}, given that
	{@html katexify(initial+".",false)}
</p>
{/if}



